import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api/client";
import { Alert } from "../components/Ui";

const STATUS_OPTIONS = ["pending", "success", "review", "partial", "refunded", "failed"];
const STATUS_STYLES = {
  success: { bg: "rgba(52,211,153,.1)", color: "#34d399", label: "Delivered" },
  pending: { bg: "rgba(251,191,36,.1)", color: "#fbbf24", label: "Processing" },
  review: { bg: "rgba(109,92,246,.1)", color: "#a78bfa", label: "Under Review" },
  partial: { bg: "rgba(251,191,36,.1)", color: "#fbbf24", label: "Partial" },
  refunded: { bg: "rgba(96,165,250,.1)", color: "#60a5fa", label: "Refunded" },
  failed: { bg: "rgba(244,63,94,.1)", color: "#f43f5e", label: "Failed" },
};

const LEG_STATUS = ["PENDING", "SENT", "DELIVERED", "FAILED", "UNKNOWN"];

export default function AdminOrderDetail() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = () => api.get(`/admin/orders/${orderId}`).then((r) => setOrder(r.data.order)).catch((e) => setError(e.message));

  useEffect(() => { load(); }, [orderId]);

  async function updateStatus(newStatus) {
    if (!window.confirm(`Change order status to "${newStatus}"?`)) return;
    setBusy(true); setError(null); setSaved(false);
    try {
      if (newStatus === "success") {
        await api.post(`/admin/orders/${orderId}/deliver`);
      } else if (newStatus === "refunded") {
        await api.post(`/admin/orders/${orderId}/refund`);
      } else {
        await api.patch(`/admin/orders/${orderId}/status`, { status: newStatus });
      }
      setSaved(true);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function updateFulfillment(index, field, value) {
    setBusy(true); setError(null); setSaved(false);
    try {
      await api.patch(`/admin/orders/${orderId}/fulfillment/${index}`, { [field]: value });
      setSaved(true);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (error && !order) return <div className="center muted" style={{ padding: "15vh 16px" }}>{error}</div>;
  if (!order) return <div className="center muted" style={{ padding: "15vh 16px" }}>Loading…</div>;

  const s = STATUS_STYLES[order.status] || STATUS_STYLES.pending;

  return (
    <div style={{ maxWidth: 640 }}>
      <Link to="/admin/orders" className="muted small" style={{ display: "block", marginBottom: 16 }}>← All orders</Link>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: "0 0 4px", fontSize: 22 }}>{order.p_info}</h1>
          <span className="mono muted small">#{order.orderId}</span>
        </div>
        <span style={{
          padding: "6px 16px", borderRadius: 99, fontSize: 13, fontWeight: 700,
          background: s.bg, color: s.color,
        }}>
          {s.label}
        </span>
      </div>

      {/* Order Details */}
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <h3 style={{ margin: "0 0 14px", fontSize: 14 }}>Order Details</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 20px" }}>
          <KV label="Pack / Amount" value={order.amount || order.p_info} />
          <KV label="Price" value={`₹${order.price}`} bold />
          <KV label="Customer" value={order.customer_name || "—"} />
          <KV label="Email" value={order.customer_email || "—"} />
          <KV label="User ID" value={order.userId || "—"} mono />
          <KV label="Zone ID" value={order.zoneId || "—"} mono />
          <KV label="Mobile" value={order.customer_mobile || "—"} />
          <KV label="Created" value={new Date(order.createdAt).toLocaleString("en-IN")} />
        </div>
      </div>

      {/* Fulfillment */}
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <h3 style={{ margin: "0 0 14px", fontSize: 14 }}>Fulfillment</h3>
        {order.fulfillment && order.fulfillment.map((leg, i) => (
          <div key={i} style={{
            padding: 12, background: "var(--ink-600, #1e2330)", borderRadius: 10,
            marginBottom: i < order.fulfillment.length - 1 ? 10 : 0,
          }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 13 }}>
              <KV label="Provider" value={leg.provider || "—"} />
              <KV label="Product ID" value={leg.productId || "—"} mono />
              <KV label="Provider Order" value={leg.providerOrderId || "—"} mono />
              <KV label="Attempted" value={leg.attemptedAt ? new Date(leg.attemptedAt).toLocaleString("en-IN") : "—"} />
              {leg.error && <div style={{ gridColumn: "1 / -1" }}><KV label="Error" value={leg.error} style={{ color: "#f43f5e" }} /></div>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
              <span className="muted small">Status:</span>
              <select className="input" value={leg.status || "PENDING"} disabled={busy}
                onChange={(e) => updateFulfillment(i, "status", e.target.value)}
                style={{ width: 160, padding: "4px 8px", fontSize: 12 }}>
                {LEG_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>

      {/* Change Status */}
      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ margin: "0 0 14px", fontSize: 14 }}>Change Order Status</h3>
        <Alert>{error}</Alert>
        {saved && <p style={{ color: "#34d399", fontSize: 13, marginBottom: 12 }}>Updated successfully.</p>}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {STATUS_OPTIONS.map((st) => (
            <button key={st} className="btn ghost" disabled={busy || order.status === st}
              onClick={() => updateStatus(st)}
              style={{
                padding: "6px 14px", fontSize: 12, fontWeight: 700,
                opacity: order.status === st ? 0.4 : 1,
                color: (STATUS_STYLES[st] || {}).color || "var(--text)",
                borderColor: (STATUS_STYLES[st] || {}).color || "var(--edge)",
              }}>
              {(STATUS_STYLES[st] || {}).label || st}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function KV({ label, value, bold, mono, style }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--mute, #8a93a6)", marginBottom: 2 }}>{label}</div>
      <div style={{
        fontSize: 13, fontWeight: bold ? 800 : 600,
        fontFamily: mono ? "monospace" : "inherit",
        ...(style || {}),
      }}>{value}</div>
    </div>
  );
}
